import { IAgent } from '../resources/istatement/agent';
import { IAccount, isAccount } from '../resources/istatement/account';

export class Account implements IAccount {
	public homePage?: string;
	public name?: string;

	constructor(cfg: IAccount = {}) {
		this.homePage = cfg.homePage;
		this.name = cfg.name;
	}

	asVersion(version: string = '1.0.3'): IAccount {
		const { homePage, name } = this;
		return { homePage, name };
	}

	toString(): string {
		if (this.homePage) {
			return this.homePage;
		}
		if (this.name) {
			return this.name;
		}
		return '';
	}
}

export interface AgentParams extends Omit<IAgent, 'objectType'> {
	lastName?: string;
	firstName?: string;
	familyName?: string;
	givenName?: string;
	account?: IAccount | Account;
	[key: string]: any;
}

export class Agent implements IAgent {
	public objectType = 'Agent';
	public name?: string;
	public mbox?: string;
	public mbox_sha1sum?: string;
	public openid?: string | undefined;
	public account?: Account | undefined;
	public degraded: boolean;

	constructor(cfg?: AgentParams) {
		this.degraded = false;

		this.init(cfg);
	}

	init(cfg: AgentParams = {}): void {
		const directProps = ['name', 'mbox', 'mbox_sha1sum', 'openid'];

		// handle .9 split names and array properties into single interface
		if (typeof cfg.lastName !== 'undefined' || typeof cfg.firstName !== 'undefined') {
			cfg.name = '';
			if (typeof cfg.firstName !== 'undefined' && cfg.firstName.length > 0) {
				cfg.name = cfg.firstName[0];
				if (cfg.firstName.length > 1) {
					this.degraded = true;
				}
			}

			if (cfg.name !== '') {
				cfg.name += ' ';
			}

			if (typeof cfg.lastName !== 'undefined' && cfg.lastName.length > 0) {
				cfg.name += cfg.lastName[0];
				if (cfg.lastName.length > 1) {
					this.degraded = true;
				}
			}
		} else if (typeof cfg.familyName !== 'undefined' || typeof cfg.givenName !== 'undefined') {
			cfg.name = '';
			if (typeof cfg.givenName !== 'undefined' && cfg.givenName.length > 0) {
				cfg.name = cfg.givenName[0];
				if (cfg.givenName.length > 1) {
					this.degraded = true;
				}
			}

			if (cfg.name !== '') {
				cfg.name += ' ';
			}

			if (typeof cfg.familyName !== 'undefined' && cfg.familyName.length > 0) {
				cfg.name += cfg.familyName[0];
				if (cfg.familyName.length > 1) {
					this.degraded = true;
				}
			}
		}

		if (cfg.account && isAccount(cfg.account)) {
			if (cfg.account instanceof Account) {
				this.account = cfg.account;
			} else {
				this.account = new Account(cfg.account);
			}
		}

		for (const prop of directProps) {
			if (cfg[prop]) {
				let val: string = cfg[prop];
				if (prop === 'mbox' && !val.startsWith('mailto:')) {
					val = `mailto:${val}`;
				}
				(this as any)[prop] = val;
			}
		}
	}

	toString(lang?: string): string {
		if (this.name) {
			return this.name;
		}
		if (this.mbox) {
			return this.mbox.replace('mailto:', '');
		}
		if (this.mbox_sha1sum) {
			return this.mbox_sha1sum;
		}
		if (this.openid) {
			return this.openid;
		}
		if (this.account) {
			return this.account.toString();
		}

		return `${this.objectType}: unidentified`;
	}

	asVersion(version: string = '1.0.3'): IAgent {
		const { objectType, mbox, mbox_sha1sum, openid, name } = this;
		const result: IAgent = {
			objectType,
			name,
			mbox,
			mbox_sha1sum,
			openid
		};
		if (this.account) {
			result.account = this.account.asVersion(version);
		}

		return result;
	}

	static fromJSON(agentJSON: string): Agent {
		const _agent = JSON.parse(agentJSON);

		return new Agent(_agent);
	}
}
